import { useEffect, useState } from "react";
import Layout from "../../components/Layout";
import VehiclesSubNav from "./VehiclesSubNav";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useI18n } from "../../i18n/I18nProvider";
import { toast } from "sonner";
import { FileText, Clock, DollarSign, Download, Mail, CheckCircle, AlertTriangle, Wrench, Truck } from "lucide-react";

function toCsv(rows) {
  const headers = [
    "id", "source_type", "regnr", "resource_name", "project_name",
    "responsible_user_name", "start", "slut", "duration_hours",
    "hourly_rate", "total_cost", "email_sent", "created_at",
  ];
  const escape = (v) => {
    if (v === null || v === undefined) return "";
    const s = String(v).replace(/"/g, '""');
    return /[;\n"]/.test(s) ? `"${s}"` : s;
  };
  const head = headers.join(";");
  const body = rows.map((r) => headers.map((h) => escape(r[h])).join(";")).join("\n");
  return `\uFEFF${head}\n${body}`;
}

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="border border-[var(--ink-200)] bg-[var(--bone)] p-5">
      <div className="label-tech inline-flex items-center gap-2"><Icon size={12} /> {label}</div>
      <div className="font-display text-3xl tracking-tighter mt-1">{value}{suffix && <span className="text-base ml-1 text-[var(--ink-500)]">{suffix}</span>}</div>
    </div>
  );
}

export default function RentalReports() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState({ items: [], total_hours: 0, total_cost: 0, count: 0 });
  const [sourceType, setSourceType] = useState("");
  const [days, setDays] = useState(90);

  const load = async () => {
    const params = {};
    if (sourceType) params.source_type = sourceType;
    if (days) params.days = days;
    try {
      const { data: d } = await api.get("/rental-reports", { params });
      setData(d);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  useEffect(() => { if (user) load(); }, [user, sourceType, days]); // eslint-disable-line

  const resend = async (id) => {
    try {
      await api.post(`/rental-reports/${id}/resend-email`);
      toast.success(t("veh.uploaded"));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("dash.failed"));
    }
  };

  const exportCsv = () => {
    const blob = new Blob([toCsv(data.items)], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rental_reports_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!user) return <Layout><div className="max-w-[1440px] mx-auto p-10 label-tech">{t("add.loginRequired")}</div></Layout>;

  return (
    <Layout>
      <div className="max-w-[1440px] mx-auto px-6 pt-10 pb-16">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <div className="label-tech">{t("veh.label")}</div>
            <h1 className="font-display text-4xl md:text-5xl font-medium tracking-tighter mt-2">{t("rrep.title")}</h1>
            <div className="text-[var(--ink-700)] mt-2 text-sm max-w-2xl">{t("rrep.subtitle")}</div>
          </div>
          <button onClick={exportCsv} disabled={!data.items.length} className="btn-ghost" data-testid="rental-export-btn">
            <Download size={14} /> {t("rrep.exportCsv")}
          </button>
        </div>

        <VehiclesSubNav />

        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-6">
          <select className="input-tech max-w-[200px]" value={sourceType} onChange={(e) => setSourceType(e.target.value)} data-testid="rental-source-filter">
            <option value="">{t("rrep.filter.all")}</option>
            <option value="machine">{t("rrep.filter.machine")}</option>
            <option value="trailer">{t("rrep.filter.trailer")}</option>
          </select>
          <select className="input-tech max-w-[200px]" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="rental-days-filter">
            <option value={30}>{t("rrep.filter.days30")}</option>
            <option value={90}>{t("rrep.filter.days90")}</option>
            <option value={365}>{t("rrep.filter.days365")}</option>
            <option value={0}>{t("rrep.filter.all_time")}</option>
          </select>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Stat icon={FileText} label={t("rrep.count")} value={data.count} />
          <Stat icon={Clock} label={t("rrep.totalHours")} value={data.total_hours.toFixed(1)} suffix="h" />
          <Stat icon={DollarSign} label={t("rrep.totalCost")} value={Math.round(data.total_cost).toLocaleString()} suffix="kr" />
        </div>

        <div className="border border-[var(--ink-200)] bg-[var(--bone)]" data-testid="rental-reports-table">
          <div className="grid grid-cols-12 gap-2 px-4 py-2 label-tech border-b border-[var(--ink-200)]">
            <div className="col-span-2">Datum</div>
            <div className="col-span-3">{t("rrep.resource")}</div>
            <div className="col-span-2">{t("rrep.project")}</div>
            <div className="col-span-2">{t("rrep.responsible")}</div>
            <div className="col-span-1 text-right">{t("rrep.duration")}</div>
            <div className="col-span-1 text-right">{t("rrep.cost")}</div>
            <div className="col-span-1 text-right">—</div>
          </div>
          {data.items.length === 0 ? (
            <div className="p-10 label-tech text-center">{t("rrep.empty")}</div>
          ) : data.items.map((r) => (
            <div key={r.id} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-[var(--ink-200)] text-sm items-center" data-testid={`rental-row-${r.id}`}>
              <div className="col-span-2 font-mono-tech">{(r.created_at || "").slice(0, 10)}</div>
              <div className="col-span-3 flex items-center gap-2">
                {r.source_type === "trailer" ? <Truck size={12} /> : <Wrench size={12} />}
                <div>
                  <div className="font-mono-tech">{r.regnr || r.resource_name}</div>
                  <div className="text-[10px] text-[var(--ink-500)]">{r.resource_name}</div>
                </div>
              </div>
              <div className="col-span-2">{r.project_name || "—"}</div>
              <div className="col-span-2 text-[var(--ink-700)]">{r.responsible_user_name || r.user_name || "—"}</div>
              <div className="col-span-1 text-right font-mono-tech">{(r.duration_hours || 0).toFixed(1)} h</div>
              <div className="col-span-1 text-right font-mono-tech text-[var(--moss)]">{r.total_cost ? Math.round(r.total_cost).toLocaleString() : "—"}</div>
              <div className="col-span-1 text-right flex justify-end items-center gap-1">
                {r.email_sent ? (
                  <span className="inline-flex items-center gap-1 text-[var(--moss)] text-[10px]" title={t("rrep.emailSent")}>
                    <CheckCircle size={12} />
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-[var(--clay)] text-[10px]" title={t("rrep.emailPending")}>
                    <AlertTriangle size={12} />
                  </span>
                )}
                {user.role === "admin" && (
                  <button onClick={() => resend(r.id)} className="btn-ghost text-[10px]" title={t("rrep.resend")} data-testid={`rental-resend-${r.id}`}>
                    <Mail size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Layout>
  );
}
